/**
 * TrendCard — série temporal de custo + faixa MoM/YoY (SP3).
 *
 * LineChart Mantine na paleta teal do DS (`inovareTeal`). O ponto parcial
 * (`is_partial`) recebe um marcador destacado. A faixa MoM/YoY usa
 * `formatPct(v, { signed })` com cor semântica (alta = red.6, queda = green.6).
 * YoY null → "—".
 *
 * Não faz fetch: recebe `TrendResponse` por prop. Cores apenas via tokens DS.
 */
import { Box, Group, Text, Stack } from '@mantine/core'
import { LineChart } from '@mantine/charts'
import { IconChartLine, IconArrowUpRight, IconArrowDownRight } from '@tabler/icons-react'

import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'
import { formatUSD, formatPct, formatMonthShort } from '@/utils/finops'
import type { TrendResponse } from '@/api/finopsDashboard'

function compactUSD(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${(value / 1_000).toFixed(1)}k`
  return `$${value.toFixed(0)}`
}

/** "2026-05-01" / "2026-05" → "May/26". */
function labelFor(periodStart: string): string {
  return formatMonthShort(periodStart.slice(0, 7))
}

/** Cor semântica do delta: alta = vermelho, queda = verde, zero/neutro = dimmed. */
function deltaColor(pct: number | null): string {
  if (pct == null || pct === 0) return 'dimmed'
  return pct > 0 ? 'red.6' : 'green.6'
}

function DeltaPill({ label, pct }: { label: string; pct: number | null }) {
  if (pct == null) {
    return (
      <Group gap={4}>
        <Text size="xs" c="dimmed" fw={500}>
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          —
        </Text>
      </Group>
    )
  }
  const color = deltaColor(pct)
  const Icon = pct > 0 ? IconArrowUpRight : IconArrowDownRight
  return (
    <Group gap={4}>
      <Text size="xs" c="dimmed" fw={500}>
        {label}
      </Text>
      <Group gap={2}>
        {pct !== 0 && <Icon size={14} color={`var(--mantine-color-${color.replace('.', '-')})`} />}
        <Text size="xs" c={color} fw={600}>
          {formatPct(pct, { signed: true })}
        </Text>
      </Group>
    </Group>
  )
}

export interface TrendCardProps {
  data: TrendResponse
  /** Altura do gráfico em px. Default 260. */
  height?: number
  title?: string
}

export function TrendCard({ data, height = 260, title = 'Tendência de custo' }: TrendCardProps) {
  const chartData = data.points.map((p) => ({
    period: labelFor(p.period_start),
    'Custo USD': p.amount_usd,
    is_partial: p.is_partial,
  }))

  return (
    <WidgetCard accent="info" p={0}>
      <CardHeader
        title={title}
        badge={data.is_partial ? { value: 'Parcial', color: 'inovareTeal' } : undefined}
      />
      <Box p="md">
        {chartData.length === 0 ? (
          <EmptyState
            icon={IconChartLine}
            title="Sem série temporal"
            description="Não há tendência de custo no escopo e período selecionados."
            accent="info"
            size="sm"
            bare
          />
        ) : (
          <Stack gap="sm">
            <Group gap="lg">
              <DeltaPill label="MoM" pct={data.mom_pct} />
              <DeltaPill label="YoY" pct={data.yoy_pct} />
            </Group>
            <LineChart
              h={height}
              data={chartData}
              dataKey="period"
              series={[{ name: 'Custo USD', color: 'inovareTeal.7' }]}
              curveType="monotone"
              tickLine="xy"
              gridAxis="xy"
              withDots
              valueFormatter={(v) => compactUSD(Number(v))}
              dotProps={{ r: 3 }}
              activeDotProps={{ r: 5 }}
              tooltipProps={{
                content: ({ payload }) => {
                  const p = payload?.[0]
                  if (!p) return null
                  const partial = p.payload.is_partial
                  return (
                    <Box
                      p="xs"
                      style={{
                        background: 'var(--mantine-color-body)',
                        border: '1px solid var(--ai-border-light)',
                        borderRadius: 'var(--mantine-radius-sm)',
                      }}
                    >
                      {String(p.payload.period)}: {formatUSD(Number(p.value))}
                      {partial && (
                        <Text size="xs" c="inovareTeal.7" fw={500}>
                          parcial
                        </Text>
                      )}
                    </Box>
                  )
                },
              }}
            />
          </Stack>
        )}
      </Box>
    </WidgetCard>
  )
}
